import {
  CriteriaApi,
  CriteriaResp,
  DateRangeFilterDto,
  SelectMultipleFilterDto, TextFilterDto
} from "@/model/api/CriteriaApi"
import {SelectMultipleFilter} from "@/model/filter/SelectMultipleFilter"
import {DateRangeFilter} from "@/model/filter/DateRangeFilter"
import {Filter} from "@/model/filter/Filter"
import {Grouping} from "@/model/grouping/Grouping"
import moment from "moment"
import {ColDef, ValueGetterParams} from "@/model/ColDef"
import {TextFilter} from "@/model/filter/TextFilter";

export class MetaService {
  static readonly dateFormat = 'YYYY-MM-DD'
  private static readonly base3DayDate = moment.utc('2000-01-01')

  private static filters0: Array<Filter> = []
  private static groupings0: Array<Grouping> = []
  private static groupingMap0: { [key: string]: Grouping } = {}
  private static defaultGroupingsNames0: Array<string> = []
  private static groupingsDate3_7: Array<string> = []

  public static baseColDef: Readonly<ColDef> = {
    width: 70,
  }

  public static formulaColDef(name: string): Readonly<ColDef> {
    return {
      ...this.baseColDef,
      colId: name
    }
  }

  public static fieldColDef(name: string): Readonly<ColDef> {
    return {
      ...this.baseColDef,
      colId: name,
      field: name
    }
  }


  public static valueGetterWrapper(fn: (data: object) => any): (params: ValueGetterParams) => any {
    return (params: ValueGetterParams) => {
      const isGroupNode = !!params.node?.group
      const data = isGroupNode ? params.node?.aggData : params.data
      if (!data) return undefined
      if ((data as any)._placeholder) return null
      return fn(data)
    }
  }

  public static decimalFormatterFactory(fraction: number): (params: any) => string {
    return (params) => {
      const v = params.value
      return v === null || v === undefined || isNaN(v) ? "-" : v.toFixed(fraction)
    }
  }

  public static percentFormatterFactory(fraction: number = 0): (params: any) => string {
    return (params) => {
      const v = params.value
      return v === null || v === undefined || isNaN(v) ? "-" : (v * 100).toFixed(fraction) + '%'
    }
  }


  static get filters(): ReadonlyArray<Filter> {
    return this.filters0
  }

  static get groupings(): ReadonlyArray<Grouping> {
    return this.groupings0
  }

  static get groupingMap(): Readonly<{ [key: string]: Grouping }> {
    return this.groupingMap0
  }

  static get defaultGroupingNames(): ReadonlyArray<string> {
    return this.defaultGroupingsNames0
  }

  static get isMetaLoaded(): boolean {
    return this.filters.length != 0 && this.groupings.length != 0
  }

  static async updateMeta(): Promise<boolean> {
    let data: CriteriaResp
    try {
      const resp = await CriteriaApi.execute()
      data = resp.data
    } catch (e) {
      return false
    }
    this.defaultGroupingsNames0 = data.defaultGroupings
    this.groupingsDate3_7 = data.groupingsDate3_7
    this.fillFiltersFromCriteriaResult(data.filters)
    this.fillGroupingsFromCriteriaResult(data.groupings)
    return true
  }

  static fillFiltersFromCriteriaResult(filters: Array<TextFilterDto | SelectMultipleFilterDto | DateRangeFilterDto>) {
    this.filters0 = []
    for (const dto of filters) {
      if (dto.type == "text") this.filters0.push(new TextFilter(<TextFilterDto>dto))
      else if (dto.type == "multiSelect") this.filters0.push(new SelectMultipleFilter(<SelectMultipleFilterDto>dto))
      else if (dto.type == "dateRange") this.filters0.push(new DateRangeFilter(<DateRangeFilterDto>dto))
      else {
        console.warn(`Unknown filter type: "${dto.type}"`)
      }
    }
  }

  static fillGroupingsFromCriteriaResult(groupings: Array<string>) {
    this.groupings0 = []
    for (const grouping of groupings) {
      this.groupings0.push({
        name: grouping,
        serverField: grouping
      })

      if (this.groupingsDate3_7.includes(grouping)) {
        this.groupings0.push({
          name: `${grouping}3`,
          serverField: grouping,
          valueGetter: ({data}: any) => {
            if (!data || !data[grouping]) return undefined

            const days = moment.utc(data[grouping], this.dateFormat).diff(this.base3DayDate, 'days')
            return this.base3DayDate.clone().add(days - (days % 3), 'days').format(this.dateFormat)
          }
        })

        this.groupings0.push({
          name: `${grouping}7`,
          serverField: grouping,
          valueGetter: ({data}: any) => {
            if (!data || !data[grouping]) return undefined

            const date = moment.utc(data[grouping], this.dateFormat)
            return date.subtract(date.weekday(), 'days').format(this.dateFormat)
          }
        })
      }
    }

    this.groupingMap0 = {}
    for (const grouping of this.groupings) {
      this.groupingMap0[grouping.name] = grouping
    }
  }
}
